import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import CachedIcon from "@mui/icons-material/Cached";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AddIcon from "@mui/icons-material/Add";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "./firebase";
import { getStyles } from "../index";
import { useSnackbar } from "./SnackbarHandler";
import EssayList from "./EssayList";
import ClassDropdown from "./ClassDropdown";
import StatusFilters from "./StatusFilters";
import AssessmentStats from "./AssessmentStats";
import Logger from "./Logger";

const Essays = ({ user }) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Inside the Essays component function, add:
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showQueued = queryParams.get("showQueued") === "true";

  // Update the statusFilter state initialization
  const [statusFilter, setStatusFilter] = useState(showQueued ? "queued" : "");

  useEffect(() => {
    fetchEssays();
  }, [user]);

  // Add a new useEffect for real-time listening to queued essays
  useEffect(() => {
    if (!user || essays.length === 0) return;

    // Find queued essays to listen for updates
    const queuedEssays = essays.filter((essay) => essay.queued);

    // If no queued essays, we don't need listeners
    if (queuedEssays.length === 0) return;

    // Set up listeners for each queued essay
    const unsubscribes = queuedEssays.map((essay) => {
      const essayRef = doc(db, "essays", essay.id);

      return onSnapshot(
        essayRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const updatedEssay = {
              ...essay, // First spread the original essay to keep ALL properties
              ...docSnapshot.data(), // Then override with the new data
              id: docSnapshot.id,
              className: essay.className, // Ensure className is preserved
            };

            // Update the essay in our local state
            setEssays((prevEssays) =>
              prevEssays.map((prevEssay) =>
                prevEssay.id === updatedEssay.id ? updatedEssay : prevEssay,
              ),
            );
          }
        },
        (error) => {
          console.error("Error listening for essay updates:", error);
        },
      );
    });

    // Clean up listeners when component unmounts
    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [essays, user]);

  const fetchEssays = async () => {
    if (!user) return;

    setLoading(true);
    const essaysRef = collection(db, "essays");
    const q = query(
      essaysRef,
      where("userId", "==", user.uid),
      orderBy("createdAt", "desc"),
    );

    try {
      const querySnapshot = await getDocs(q);
      const essayList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Hämta alla unika classIds
      const classIds = [
        ...new Set(
          essayList
            .filter((essay) => essay.classId)
            .map((essay) => essay.classId),
        ),
      ];

      // Hämta klassdata för alla unika classIds
      const classesData = {};
      if (classIds.length > 0) {
        const classesSnapshot = await Promise.all(
          classIds.map((classId) => getDoc(doc(db, "classes", classId))),
        );

        classesSnapshot.forEach((snap) => {
          if (snap.exists()) {
            classesData[snap.id] = snap.data();
          }
        });
      }

      // Lägg till klassnamn till varje essay
      const essaysWithClassNames = essayList.map((essay) => ({
        ...essay,
        className: essay.classId
          ? classesData[essay.classId]?.name || "-"
          : "-",
      }));

      setEssays(essaysWithClassNames);

      Logger.log("essaysWithClassNames: ", essaysWithClassNames);
    } catch (error) {
      console.error("Error fetching essays: ", error);
      showSnackbar("Ett fel uppstod när uppsatserna skulle hämtas.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEssaysDelete = async (essaysToDelete) => {
    try {
      await Promise.all(
        essaysToDelete.map((essay) => deleteDoc(doc(db, "essays", essay.id))),
      );

      setEssays((prevEssays) =>
        prevEssays.filter(
          (essay) =>
            !essaysToDelete.some((toDelete) => toDelete.id === essay.id),
        ),
      );

      showSnackbar(
        `${essaysToDelete.length} ${
          essaysToDelete.length === 1 ? "text" : "texter"
        } raderade.`,
        "success",
      );
    } catch (error) {
      console.error("Error deleting essays:", error);
      showSnackbar("Ett fel uppstod vid radering av texter.", "error");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.innerContainer}>
        <Typography
          variant="h1"
          sx={{
            ...styles.newH1,
            width: { xs: "100%", md: "25%" },
            mt: { xs: 0, md: "inherit" },
            textAlign: "left",
            width: "100%",
            pl: { xs: 6, md: 0 },
            fontSize: { xs: "1.1rem", md: "1.7rem" },
            fontWeight: { xs: 500, md: 600 },
          }}
        >
          Mina bedömningar
        </Typography>

        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            startIcon={isMobile ? "" : <AddIcon />}
            component={Link}
            sx={{ ...styles.tightButton }}
            to={`/app/uppsats/ny`}
          >
            Bedöm text
          </Button>
        </Box>

        <StatusFilters
          essays={essays}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          theme={theme}
        />

        {essays.length === 0 ? (
          <>
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              Du har inte bedömt några texter ännu.
            </Typography>
          </>
        ) : (
          <EssayList
            essays={essays}
            statusFilter={statusFilter}
            onEssaysDelete={handleEssaysDelete}
            onEssayUpdate={fetchEssays}
            theme={theme}
            styles={styles}
          />
        )}
      </Box>
    </Box>
  );
};

export default Essays;
